import { useState } from "react";
import { AppBar, Box, Container, Toolbar } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useNavigate } from "react-router-dom";
import HideOnScroll from "../../components/HideOnScroll.js";
import HeaderMenuButton from "../../components/HeaderMenuButton.js";
import ListMenu from "../../components/ListMenu.js";
import SearchFilter from "../../components/SearchFilter.js";
import { HeaderButton } from "../../components/CustomMUI.js";
import {
  getContentOptions,
  getContentOptionsArray,
  getContentTypeSlug,
} from "../../utils/utils.js";

/**
 * Header for the explore drill-down pages (ExploreActivity, ExploreCollections).
 *
 * Unlike the explore home header (which lays the content types out as pills), this
 * mirrors the curate/track headers: a single content-type dropdown button. The
 * selected content type lives in the URL, so selecting an option navigates rather
 * than setting state — `basePath` + the option's slug rebuild the route the same
 * way the pill header did, keeping ExploreDispatcher routing unchanged.
 *
 * SearchFilter is rendered only when `onSearchTermChange` is supplied (collections
 * only — the activity feed has no server-side title search).
 */
export default function ExploreSecondaryHeader({
  toggleDrawer,
  drawerWidth,
  activeFilter,
  basePath = "/explore",
  searchTerm,
  onSearchTermChange,
}) {
  const navigate = useNavigate();
  const [contentAnchorEl, setContentAnchorEl] = useState(null);

  const contentOptions = getContentOptions({ showEverythingOption: true });
  const contentOptionsArray = getContentOptionsArray({
    showEverythingOption: true,
  }).map((option) => ({ ...option, label: option.labelPlural }));

  const handleContentClick = (event) => setContentAnchorEl(event.currentTarget);

  const handleContentSelect = (value) => () => {
    // "all" has no slug → drop back to the bare basePath; content types append theirs
    const slug = getContentTypeSlug(value);
    navigate(slug ? `${basePath}/${slug}` : basePath);
    setContentAnchorEl(null);
  };

  return (
    <>
      {/* Spacer — keeps content from sliding under the fixed AppBar */}
      <Toolbar sx={{ mt: 2.5, mb: 2.5 }} />
      <HideOnScroll>
        <AppBar
          color="inherit"
          sx={{
            boxShadow: "none",
            backgroundImage: "none",
            width: { md: `calc(100% - ${drawerWidth}px)` },
            ml: { md: `${drawerWidth}px` },
            pt: 2,
            pb: 2,
            overflowX: "auto",
            // Hide the scrollbar visually while keeping scroll functionality
            "&::-webkit-scrollbar": { display: "none" },
            msOverflowStyle: "none",
            scrollbarWidth: "none",
          }}
        >
          <Container maxWidth="lg">
            <Toolbar disableGutters>
              <HeaderMenuButton onClick={toggleDrawer} sx={{ mr: 2 }} />

              <Box
                sx={{
                  display: "flex",
                  flexGrow: 1,
                  flexShrink: 0,
                  alignItems: "center",
                }}
              >
                <HeaderButton
                  onClick={() => navigate("/explore")}
                  sx={{ mr: 1, flexShrink: 0 }}
                  aria-label="Back to explore"
                  startIcon={<ArrowBackIcon />}
                >
                  Explore
                </HeaderButton>

                <HeaderButton
                  startIcon={contentOptions[activeFilter]?.icon}
                  endIcon={<ExpandMoreIcon />}
                  onClick={handleContentClick}
                  sx={{ mr: 1, flexShrink: 0 }}
                >
                  {contentOptions[activeFilter]?.labelPlural}
                </HeaderButton>
              </Box>

              {onSearchTermChange && (
                <SearchFilter
                  searchTerm={searchTerm}
                  onSearchTermChange={onSearchTermChange}
                  alignEnd
                />
              )}
            </Toolbar>
          </Container>
        </AppBar>
      </HideOnScroll>

      {contentAnchorEl && (
        <ListMenu
          anchorEl={contentAnchorEl}
          onClose={() => setContentAnchorEl(null)}
          onSelect={handleContentSelect}
          options={contentOptionsArray}
          selectedValue={activeFilter}
        />
      )}
    </>
  );
}
